import { useState } from 'react';
import {
    Popover, Box, Typography, Button, Checkbox, Tooltip, Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ALERT_TYPE_LABELS } from '../pages/markets/components/AlertRow';
import { ALERT_SEVERITY_COLORS } from '../pages/markets/utils/colors';
import { SEVERITY_LABEL, SEVERITY_TOOLTIP, SEVERITY_ORDER } from './severityLabel';
import type { AlertSeverity } from '../pages/markets/api/types';

const TYPE_TYPICAL_SEVERITY: Record<string, AlertSeverity> = {
    large_trade: 'info',
    probability_spike: 'warning',
    volume_anomaly: 'info',
    insider_capital_threshold: 'warning',
    new_insider_admission: 'info',
    insider_position_closed: 'info',
    large_position_change: 'info',
    edge_asymmetry: 'critical',
    winner_vs_all_traders_edge: 'critical',
};

const TYPE_TOOLTIP: Record<string, string> = {
    large_trade: 'Info — single trade above the size threshold for this market.',
    probability_spike: 'Warning — implied probability moved sharply in a short window.',
    volume_anomaly: 'Info — trade volume above the rolling baseline.',
    insider_capital_threshold: 'Warning — tracked insiders deployed significant capital on one side.',
    new_insider_admission: 'Info — a new trader was admitted to the insider watchlist.',
    insider_position_closed: 'Info — a tracked insider closed their position.',
    large_position_change: 'Info — a tracked insider materially changed position size.',
    edge_asymmetry: 'Critical — strong asymmetric edge between groups.',
    winner_vs_all_traders_edge: 'Critical — winning traders show a large edge over the rest of the market.',
};

const TYPE_GROUPS: Array<{ title: string; types: string[] }> = [
    { title: 'Market activity', types: ['large_trade', 'probability_spike', 'volume_anomaly'] },
    { title: 'Insider / conviction', types: ['insider_capital_threshold', 'new_insider_admission', 'insider_position_closed', 'large_position_change'] },
    { title: 'Edge', types: ['edge_asymmetry', 'winner_vs_all_traders_edge'] },
];

const ALL_TYPE_KEYS = TYPE_GROUPS.flatMap(g => g.types);

export interface SubscribePopoverProps {
    open: boolean;
    anchorEl: Element | null;
    onClose: () => void;
    mode: 'create' | 'edit';
    initialAlertTypes: string[] | null;
    initialMinSeverity: AlertSeverity;
    onSave: (payload: { alert_types: string[] | null; min_severity: AlertSeverity }) => Promise<void>;
    onUnsubscribe?: () => Promise<void>;
}

export function SubscribePopover(props: SubscribePopoverProps) {
    const theme = useTheme();
    const { mode, initialAlertTypes, initialMinSeverity } = props;

    const [selectedTypes, setSelectedTypes] = useState<Set<string>>(
        initialAlertTypes === null ? new Set(ALL_TYPE_KEYS) : new Set(initialAlertTypes)
    );
    const [minSeverity, setMinSeverity] = useState<AlertSeverity>(initialMinSeverity);
    const [saving, setSaving] = useState(false);

    const allSelected = selectedTypes.size === ALL_TYPE_KEYS.length;

    function toggleType(t: string) {
        setSelectedTypes(prev => {
            const next = new Set(prev);
            if (next.has(t)) next.delete(t); else next.add(t);
            return next;
        });
    }

    function toggleAll() {
        setSelectedTypes(allSelected ? new Set() : new Set(ALL_TYPE_KEYS));
    }

    async function handleSave() {
        setSaving(true);
        try {
            const alert_types = allSelected ? null : Array.from(selectedTypes);
            await props.onSave({ alert_types, min_severity: minSeverity });
            props.onClose();
        } finally {
            setSaving(false);
        }
    }

    async function handleUnsubscribe() {
        if (!props.onUnsubscribe) return;
        setSaving(true);
        try {
            await props.onUnsubscribe();
            props.onClose();
        } finally {
            setSaving(false);
        }
    }

    const buttonLabel = mode === 'create'
        ? `Subscribe${allSelected ? '' : ` (${selectedTypes.size})`}`
        : 'Save';

    return (
        <Popover
            open={props.open}
            anchorEl={props.anchorEl}
            onClose={props.onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { width: 340 } } }}
        >
            <Box sx={{ p: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" fontWeight={600}>Notify me about</Typography>
                <Button size="small" onClick={toggleAll} sx={{ minWidth: 0, fontSize: '0.7rem' }}>
                    {allSelected ? 'Select none' : 'Select all'}
                </Button>
            </Box>

            <Box sx={{ maxHeight: 280, overflowY: 'auto', py: 0.5 }}>
                {TYPE_GROUPS.map(group => (
                    <Box key={group.title}>
                        <Typography variant="caption" sx={{ display: 'block', px: 2, pt: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            {group.title}
                        </Typography>
                        {group.types.map(t => (
                            <Box
                                key={t}
                                onClick={() => toggleType(t)}
                                sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                            >
                                <Checkbox
                                    size="small"
                                    checked={selectedTypes.has(t)}
                                    onChange={() => toggleType(t)}
                                    onClick={e => e.stopPropagation()}
                                    sx={{ p: 0 }}
                                />
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                    {ALERT_TYPE_LABELS[t] ?? t}
                                </Typography>
                                <Tooltip title={TYPE_TOOLTIP[t] ?? ''} arrow>
                                    <Box sx={{
                                        width: 9, height: 9, borderRadius: '50%',
                                        bgcolor: ALERT_SEVERITY_COLORS[TYPE_TYPICAL_SEVERITY[t]] || theme.palette.text.secondary,
                                        cursor: 'help',
                                    }} />
                                </Tooltip>
                            </Box>
                        ))}
                    </Box>
                ))}
            </Box>

            <Divider />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Min severity</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                    {SEVERITY_ORDER.map(s => (
                        <Tooltip key={s} title={SEVERITY_TOOLTIP[s]} arrow>
                            <Button
                                size="small"
                                variant={minSeverity === s ? 'contained' : 'outlined'}
                                onClick={() => setMinSeverity(s)}
                                sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: '0.7rem' }}
                            >
                                {SEVERITY_LABEL[s]}
                            </Button>
                        </Tooltip>
                    ))}
                </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, bgcolor: 'background.paper', borderTop: `1px solid ${theme.palette.divider}` }}>
                {mode === 'edit' && props.onUnsubscribe ? (
                    <Button onClick={handleUnsubscribe} disabled={saving} size="small" color="inherit" sx={{ color: 'text.secondary' }}>
                        Unsubscribe
                    </Button>
                ) : <span />}
                <Button onClick={handleSave} disabled={saving || selectedTypes.size === 0} variant="contained" size="small">
                    {buttonLabel}
                </Button>
            </Box>
        </Popover>
    );
}
